import type { Control, FieldValues, Path } from 'react-hook-form';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';

interface PcmpCommentFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  description?: string;
  /** The API's limit for this field. */
  max?: number;
}

/** A free-text comment the API records on the proposal's timeline. */
export function PcmpCommentField<T extends FieldValues>({
  control,
  name,
  label,
  required,
  placeholder,
  description = "Shown on the proposal's timeline.",
  max = PCMP_MKT_COMMENT_MAX,
}: PcmpCommentFieldProps<T>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <div className="flex items-center justify-between gap-2">
            <FormLabel>
              {label}
              {required ? <span className="text-destructive"> *</span> : null}
            </FormLabel>
            <CharacterCounter current={String(field.value ?? '').length} max={max} />
          </div>
          <FormControl>
            <Textarea {...field} rows={4} maxLength={max} placeholder={placeholder} />
          </FormControl>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
